'use client';

import { useEffect, useState } from 'react';
import { DEFAULT_SNACKBAR_DURATION } from '@/constants/static';
import { animationSpring, animationTimingStyle } from '@/styles/animation';
import { AlertColor } from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import LocalizedLink from '../LocalizedLink';
import NotificationAlert from '../NotificationAlert';

interface ToastProps {
	message: string;
	linkText?: string;
	severity: AlertColor;
	open: boolean;
	linkUrl?: string;
	onClose: () => void;
}

const Toast = ({ message, linkText, severity, open, onClose, linkUrl }: ToastProps) => {
	const theme = useTheme();
	const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
	const [isAnimating, setIsAnimating] = useState(false);

	useEffect(() => {
		let timeout: NodeJS.Timeout | null = null;
		if (open) {
			setIsAnimating(true);
			timeout = setTimeout(() => {
				setIsAnimating(false);
			}, DEFAULT_SNACKBAR_DURATION);
		}

		return () => {
			if (timeout) {
				clearTimeout(timeout);
			}
		};
	}, [open]);

	return (
		<Snackbar
			open={open}
			autoHideDuration={DEFAULT_SNACKBAR_DURATION}
			onClose={onClose}
			anchorOrigin={{ vertical: isMobileOrTablet ? 'bottom' : 'top', horizontal: 'center' }}
			sx={{
				'& .MuiAlert-root': {
					animation: isAnimating ? animationSpring : 'none',
					animationTimingFunction: animationTimingStyle,
				},
			}}
		>
			<NotificationAlert severity={severity}>
				<Box sx={{ display: 'inline-flex', gap: '40px' }}>
					{message}
					{linkText && linkUrl && (
						<LocalizedLink href={linkUrl} underline="none" color="primary.main">
							{linkText}
						</LocalizedLink>
					)}
				</Box>
			</NotificationAlert>
		</Snackbar>
	);
};

export default Toast;
